// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { sizeVariantValues } from "@ces/MobiusOverrides/FormField/formStyles";
import { TooltipTriggerer } from "@ces/MobiusOverrides/Tooltip/Tooltip";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { xBaseTheme } from "@ces/XDL/types";
import { color, ellipsis } from "@ces/XDL/Utilities";
import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

export const PlaceholderSelector = `
        &::placeholder,
        &::-webkit-input-placeholder
`;

export const placeholderStyle = css`
    ${PlaceholderSelector} {
        font-size: 14px;
        font-style: italic;
        letter-spacing: 0;
        line-height: 16px !important;
        max-width: 100%;
        color: ${color("common.ui4")};
        font-family: ${fontConfig.fontFamily};
        font-weight: ${fontConfig.weight.regular};
    }
`;

export default {
    defaultProps: {
        border: "rounded",
        sizeVariant: "default",
        backgroundColor: "common.ui1",
        labelProps: {
            mergeCss: true,
            size: 12,
            transform: "capitalize",
            weight: fontConfig.weight.bold,
            color: "text",
            css: css`
                display: flex;
                align-items: center;
                min-height: initial;
                letter-spacing: 0;
                line-height: 14px;
                .mandatory {
                    color: ${color("danger")};
                    margin-left: 4px;
                }
                ${TooltipTriggerer} {
                    margin-left: 8px;
                    ${IconWrapper}, ${IconWrapper} svg {
                        width: 12px;
                        height: 13px;
                        color: ${color("text.link")};
                        opacity: 0.66;
                        &:hover {
                            opacity: 1;
                        }
                        &:disabled {
                            color: ${color("common.ui4")};
                        }
                    }
                }
            `,
        },
        errorProps: {
            mergeCss: true,
            size: 14,
            color: "text",
            weight: fontConfig.weight.regular,
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                input,
                textarea,
                select {
                    transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
                    ${placeholderStyle}

                    &:not(:disabled):not(:focus):hover {
                        border: 1px solid ${color("common.ui8")};
                    }

                    &,
                    &:focus {
                        &:has(+ ${IconWrapper}) {
                            ${({ _iconPosition = "right", _sizeVariant }: any) => {
                                return `
                                    ${
                                        _iconPosition === "right"
                                            ? `padding-right: ${
                                                  sizeVariantValues[_sizeVariant as keyof typeof sizeVariantValues]
                                                      .icon + 9
                                              }px;`
                                            : `padding-left: ${
                                                  sizeVariantValues[_sizeVariant as keyof typeof sizeVariantValues]
                                                      .icon + 9
                                              }px;`
                                    }

                                    `;
                            }}
                        }

                        &:has(+ ${FormFieldClickable}) {
                            padding-right: 34px;
                        }
                    }

                    &:disabled {
                        & ~ ${IconWrapper} {
                            opacity: 0.33;
                        }
                        ${PlaceholderSelector} {
                            color: ${color("common.black")};
                        }
                    }
                }

                ${IconWrapper}.pre-icon {
                    left: 0;
                    & + select {
                        padding-left: 30px;
                    }
                }

                ${IconWrapper}:not(:disabled) {
                    &:hover {
                        &,
                        svg {
                            cursor: pointer;
                            color: ${color("common.ui8")};
                        }
                    }
                }

                ${FormFieldClickable}:not(:disabled) {
                    ${FormFieldIcon} {
                        color: ${color("text.link")};
                    }
                    &:hover {
                        ${FormFieldIcon} {
                            color: ${color("common.ui8")};
                        }
                    }
                }

                input:not(.react-datetime-picker__inputGroup__input) {
                    box-shadow: inset 0 1px 0 0 ${color("common.black", 0.1)};
                }

                input,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${ellipsis(1)}
                        text-overflow: ellipsis;
                    }
                }
            `,
        },
    },
} as xBaseTheme["formField"];
